// custom: T4a — regra de resolução de nome do contato para conversas WhatsApp/Haikunator
//
// Motivação: o Chatwoot grava um nome aleatório do Haikunator (ex.: "wandering-frog-42")
// quando um contato é criado sem nome. Quando a Evolution API não tem pushName ela grava
// o número em contact.name — essa é agora a fonte confirmada do "nome" bruto do WhatsApp.
//
// Fonte confirmada (Evolution chatwoot.service.ts): pushName → contact.name diretamente.
// additional_attributes NÃO é usado pela Evolution; PUSHNAME_KEYS é mantido como tolerância
// para outros canais (canal interno, integrações futuras), mas não é mais merge-blocker.
//
// Ordem de prioridade (§4.2):
//   1) nome salvo "de verdade" (não-Haikunator, não telefone disfarçado)
//   2) pushname / perfil em additional_attributes (outros canais)
//   3) phone_number como string
//   4) fallback final (Haikunator ou caller-supplied para UNKNOWN_CONTACT)

// Heurística Haikunator: regex baseada no formato exato do ::Haikunator.haikunate(1000)
// upstream, que gera "<adjetivo>-<substantivo>-<0..999>".
// custom: T4a — Q3: regex aprovada; risco de falso-positivo em nome humano atípico documentado e aceito
const HAIKUNATOR_RE = /^[a-z]+-[a-z]+-\d{1,4}$/;

// Padrão que identifica strings compostas essencialmente só de dígitos telefônicos
// (+, dígitos, espaços, parênteses, hífens). Ex: "5511999999999", "+55 11 99999-9999".
const PHONE_CHARS_RE = /^[+\d\s\-().]+$/;

// Chaves candidatas onde OUTROS canais (não Evolution) podem gravar o pushname.
// custom: T4a — Q2 resolvido: Evolution grava pushname em contact.name diretamente
// (confirmado em chatwoot.service.ts). PUSHNAME_KEYS mantido como tolerância para
// outros canais — não bloqueia merge.
const PUSHNAME_KEYS = [
  'pushname',
  'profile_name',
  'source_name',
  'verified_name',
  'whatsapp_name',
];

/**
 * Retorna apenas os dígitos de uma string (remove qualquer não-dígito).
 * @param {unknown} val
 * @returns {string}
 */
function digitsOnly(val) {
  return String(val == null ? '' : val).replace(/\D/g, '');
}

/**
 * Retorna verdadeiro se `name` parece ter sido gerado pelo Haikunator do upstream.
 * @param {string} name
 * @returns {boolean}
 */
export function looksLikeHaikunator(name) {
  return HAIKUNATOR_RE.test(name);
}

/**
 * Retorna verdadeiro se `name` é essencialmente um número de telefone:
 * composto só de dígitos, +, espaços, parênteses e hífens, OU quando
 * seus dígitos coincidem com os dígitos de `phone`.
 *
 * @param {string} name  - já trimado
 * @param {string} phone - já convertido para string
 * @returns {boolean}
 */
export function nameIsPhoneNumber(name, phone) {
  if (!name) return false;
  // Estruturalmente parece telefone (só chars de número)
  if (PHONE_CHARS_RE.test(name)) return true;
  // Dígitos do name == dígitos do phone (cobre "5511999" vs "+5511999")
  const nameDig = digitsOnly(name);
  const phoneDig = digitsOnly(phone);
  return nameDig.length > 0 && phoneDig.length > 0 && nameDig === phoneDig;
}

/**
 * Retorna o primeiro valor presente nas chaves candidatas de pushname dentro de
 * `additional_attributes`, ou null se nenhuma chave tiver valor.
 * @param {Record<string, unknown> | null | undefined} attrs
 * @returns {string | null}
 */
function extractPushname(attrs) {
  if (!attrs || typeof attrs !== 'object') return null;
  const key = PUSHNAME_KEYS.find(k => {
    const val = attrs[k];
    return val && typeof val === 'string' && val.trim();
  });
  return key ? attrs[key].trim() : null;
}

/**
 * Resolve o nome de exibição de um contato seguindo a ordem de prioridade:
 *   1) nome salvo real (não-Haikunator, não telefone disfarçado)
 *   2) pushname / perfil em additional_attributes (outros canais)
 *   3) phone_number como string
 *   4) fallback fornecido pelo caller (normalmente $t('CONVERSATION.UNKNOWN_CONTACT'))
 *
 * Função pura: sem store, sem i18n interno. O fallback i18n fica no componente.
 *
 * @param {Object} contact - objeto do contato da store
 * @param {string} [fallback=''] - string de fallback quando tudo falha (nunca vazio no caller)
 * @returns {string}
 */
export function resolveContactName(contact, fallback = '') {
  if (!contact || typeof contact !== 'object') return fallback;

  const name = typeof contact.name === 'string' ? contact.name.trim() : '';
  const phone = String(
    contact.phone_number == null ? '' : contact.phone_number
  );
  const pushname = extractPushname(contact.additional_attributes);

  // 1) nome salvo "de verdade": presente, não-Haikunator, não telefone disfarçado
  if (name && !looksLikeHaikunator(name) && !nameIsPhoneNumber(name, phone)) {
    return name;
  }

  // 2) pushname / perfil (outros canais via additional_attributes)
  if (pushname) {
    return pushname;
  }

  // 3) número como string (coerção explícita — garante que phone_number numérico
  //    também funciona; exibe como veio, sem lib de formatação nova nesta onda)
  if (phone) {
    return phone;
  }

  // 4) último recurso — nunca retornar string vazia (Avatar e UI dependem do name)
  return name || fallback;
}
